/**
 * Applies Stamped loyalty reward codes to the Shopify cart and opens the drawer.
 *
 * Stamped's Apply button otherwise GETs /checkout?discount=CODE, which never
 * re-renders the cart drawer. Shopify.actions.updateCart emits
 * shopify:cart:discount-update, and the cart section refreshes from that.
 */

/** @type {((state: string) => void) | null} */
let pendingSetState = null;

/** @type {Map<string, Promise<void>>} */
const inFlight = new Map();

/**
 * @param {Event} event
 * @returns {string}
 */
function codeFromEvent(event) {
  const detail = /** @type {CustomEvent} */ (event).detail;

  if (typeof detail === 'string') return detail.trim();
  if (!detail || typeof detail !== 'object') return '';

  const code = detail.code || detail.rewardCampaignCoupon?.couponCode || '';
  return String(code).trim();
}

/**
 * @param {string} code
 */
function applyRewardCode(code) {
  const key = code.toLowerCase();
  const existing = key ? inFlight.get(key) : undefined;

  if (existing) {
    return existing.finally(releaseApplyButton);
  }

  const promise = applyRewardCodeOnce(code).finally(() => {
    if (key) inFlight.delete(key);
  });

  if (key) inFlight.set(key, promise);
  return promise;
}

function releaseApplyButton() {
  const setState = pendingSetState;
  pendingSetState = null;
  if (typeof setState === 'function') setState('active');
}

/**
 * @param {string} code
 */
async function applyRewardCodeOnce(code) {
  // rewardsApplyCode stores setState after this event is dispatched.
  await Promise.resolve();
  const setState = pendingSetState;
  pendingSetState = null;

  const finish = () => {
    if (typeof setState === 'function') setState('active');
  };

  try {
    const actions = window.Shopify?.actions;
    if (!code || !actions?.getCart || !actions?.updateCart) return;

    const key = code.toLowerCase();
    const current = await actions.getCart();
    const currentCodes = current.cart?.discountCodes ?? [];
    const existing = currentCodes.map((entry) => entry.code).filter(Boolean);
    const alreadyApplied = currentCodes.some(
      (entry) => entry.code.toLowerCase() === key && entry.applicable
    );

    /** @type {Awaited<ReturnType<NonNullable<typeof actions.updateCart>>> | typeof current} */
    let result = current;

    if (!alreadyApplied) {
      result = await actions.updateCart({
        discountCodes: [...existing, code],
      });

      const accepted = result.cart?.discountCodes?.some(
        (entry) => entry.code.toLowerCase() === key && entry.applicable
      );

      if (!accepted) {
        await actions.updateCart({ discountCodes: existing });
        return;
      }
    }

    const quantity = result.cart?.totalQuantity ?? 0;
    if (quantity > 0 && actions.openCart) {
      await actions.openCart();
    }
  } catch (error) {
    console.warn('[stamped-rewards-cart] Failed to apply reward code', error);
  } finally {
    finish();
  }
}

function onRewardCode(event) {
  applyRewardCode(codeFromEvent(event));
}

document.addEventListener('stamped:rewards:redeemed', onRewardCode);
document.addEventListener('stamped:rewards:coupon:applied', onRewardCode);

function patchRewardsApplyCode() {
  const stamped = window.StampedFn;
  if (!stamped || stamped.__rageismApplyPatched || typeof stamped.rewardsApplyCode !== 'function') {
    return false;
  }

  const original = stamped.rewardsApplyCode.bind(stamped);

  stamped.rewardsApplyCode = function (payload) {
    const next = Object.assign({}, payload, { skip: true });
    const result = original(next);

    if (result && typeof result.setState === 'function') {
      pendingSetState = result.setState;
    }

    return result;
  };

  stamped.__rageismApplyPatched = true;
  return true;
}

if (!patchRewardsApplyCode()) {
  const started = Date.now();
  const timer = window.setInterval(() => {
    if (patchRewardsApplyCode() || Date.now() - started > 30000) {
      window.clearInterval(timer);
    }
  }, 200);
}
